import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { TrendingUp, TrendingDown, HardHat, Wallet, Receipt, Clock, AlertTriangle, Filter, X, Calculator, Droplet, Trash2 } from "lucide-react";
import CashFlowForecast from "@/components/CashFlowForecast";
import FatturatoVsBEP from "@/components/FatturatoVsBEP";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const num = (n) => new Intl.NumberFormat("it-IT").format(n || 0);

function Kpi({ label, value, icon: Icon, tone, testId }) {
  const tones = {
    good: "border-emerald-500",
    bad: "border-red-400",
    accent: "border-orange-500 shadow-brutal",
  };
  return (
    <div data-testid={testId} className={`bg-white border p-4 ${tones[tone] || "border-slate-200"}`}>
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${tone === "good" ? "text-emerald-600" : tone === "bad" ? "text-red-600" : "text-slate-400"}`} />}
      </div>
      <div className="font-display text-xl xl:text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [filters, setFilters] = useState({
    societa: "", cantiere_id: "", cliente: "", da: "", a: "", fornitore: "", lavoratore_id: "", categoria: "", stato: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  const params = useMemo(
    () => new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v))).toString(),
    [filters]
  );
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", params],
    queryFn: () => api.get(`/admin/dashboard${params ? `?${params}` : ""}`).then((r) => r.data),
    enabled: admin,
  });
  const { data: lavoratori = [] } = useQuery({
    queryKey: ["lavoratori"],
    queryFn: () => api.get("/lavoratori").then((r) => r.data),
    enabled: admin,
  });
  const { data: cantieriAll = [] } = useQuery({
    queryKey: ["cantieri"],
    queryFn: () => api.get("/cantieri").then((r) => r.data),
    enabled: admin,
  });

  const opt = data?.filter_options || {};

  // Stable memoized options for FSelect (avoid new array refs on every render).
  // MUST be declared BEFORE any conditional return.
  const optSocieta    = useMemo(() => [["", "Tutte"], ...(opt.societa   || []).map((s) => [s, s])],  [opt.societa]);
  const optCantieri   = useMemo(() => [["", "Tutti"], ...cantieriAll.map((c) => [c.cantiere_id, c.nome])], [cantieriAll]);
  const optClienti    = useMemo(() => [["", "Tutti"], ...(opt.clienti   || []).map((c) => [c, c])],   [opt.clienti]);
  const optFornitori  = useMemo(() => [["", "Tutti"], ...(opt.fornitori || []).map((f) => [f, f])],   [opt.fornitori]);
  const optLavoratori = useMemo(() => [["", "Tutti"], ...lavoratori.map((l) => [l.lavoratore_id, `${l.nome} ${l.cognome}`])], [lavoratori]);
  const optCategorie  = useMemo(() => [["", "Tutte"], ...(opt.categorie || []).map((c) => [c, c])],   [opt.categorie]);
  const optStati      = useMemo(() => [["", "Tutti"], ...(opt.stati     || []).map((s) => [s, s])],   [opt.stati]);

  if (!admin) {
    return (
      <div>
        <h1 className="font-display text-3xl font-black mb-2">Benvenuto, {user?.name?.split(" ")[0]}</h1>
        <p className="text-slate-600 mb-8">Accedi ai tuoi cantieri assegnati dal menu laterale.</p>
      </div>
    );
  }
  if (isLoading || !data) return <div className="text-slate-500">Caricamento dashboard…</div>;

  const activeFilters = Object.values(filters).filter(Boolean).length;

  const cantieriBar = data.cantieri.slice(0, 8).map((c) => ({
    nome: c.nome.length > 20 ? c.nome.slice(0, 20) + "…" : c.nome,
    Guadagno: c.incidenza_disponibile ? c.guadagno_netto : null,
    Costo: c.costo_totale,
    Ricavo: c.importo_totale_previsto,
  }));

  return (
    <div className="space-y-8">
      <DashboardEconomicaCard />
      {admin && <CashFlowForecast />}
      {admin && <FatturatoVsBEP />}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline">Dashboard amministrativa</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black">Panoramica generale</h1>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/export/dashboard/excel`} className="text-xs font-semibold px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-sm">
            Esporta Excel
          </a>
          <button
            onClick={() => setShowFilters((s) => !s)}
            className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-sm ${activeFilters > 0 ? "bg-orange-600 text-white border-orange-600" : "border-slate-300 hover:bg-slate-50"}`}
          >
            <Filter className="w-4 h-4" /> Filtri {activeFilters > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-white text-orange-600 rounded-sm">{activeFilters}</span>}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="bg-white border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">Filtri</div>
            <button
              onClick={() => setFilters({ societa: "", cantiere_id: "", cliente: "", da: "", a: "", fornitore: "", lavoratore_id: "", categoria: "", stato: "" })}
              className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Reset
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
            <FSelect label="Società" value={filters.societa} onChange={(v)=>setFilters({...filters,societa:v})}
              options={optSocieta} />
            <FSelect label="Cantiere" value={filters.cantiere_id} onChange={(v)=>setFilters({...filters,cantiere_id:v})}
              options={optCantieri} />
            <FSelect label="Cliente" value={filters.cliente} onChange={(v)=>setFilters({...filters,cliente:v})}
              options={optClienti} />
            <FInput label="Dal" type="date" value={filters.da} onChange={(v)=>setFilters({...filters,da:v})} />
            <FInput label="Al" type="date" value={filters.a} onChange={(v)=>setFilters({...filters,a:v})} />
            <FSelect label="Fornitore" value={filters.fornitore} onChange={(v)=>setFilters({...filters,fornitore:v})}
              options={optFornitori} />
            <FSelect label="Lavoratore" value={filters.lavoratore_id} onChange={(v)=>setFilters({...filters,lavoratore_id:v})}
              options={optLavoratori} />
            <FSelect label="Categoria costo" value={filters.categoria} onChange={(v)=>setFilters({...filters,categoria:v})}
              options={optCategorie} />
            <FSelect label="Stato commessa" value={filters.stato} onChange={(v)=>setFilters({...filters,stato:v})}
              options={optStati} />
          </div>
        </div>
      )}

      {/* All 19 KPIs required */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        <Kpi label="Fatturato" value={eur(data.fatturato)} icon={Wallet} tone="accent" />
        <Kpi label="Costi totali" value={eur(data.costi)} icon={Receipt} />
        <Kpi label="Utile" value={data.cantieri?.length > 0 && data.cantieri.every(c => c.incidenza_disponibile) ? eur(data.utile) : "Da configurare"} icon={TrendingUp} />
        <Kpi label="Margine medio" value={data.cantieri?.length > 0 && data.cantieri.every(c => c.incidenza_disponibile) ? `${data.margine_medio_pct}%` : "Da configurare"} />
        <Kpi label="Liquidità 30gg (stimata)" value={eur(data.liquidita_stimata_30gg)} icon={Droplet} tone={data.liquidita_stimata_30gg>=0?"good":"bad"} />
        <Kpi label="Crediti" value={eur(data.crediti)} icon={TrendingUp} tone="good" />
        <Kpi label="Debiti" value={eur(data.debiti)} icon={TrendingDown} tone="bad" />
        <Kpi label="Costi di gestione mensili" value={eur(data.costi_fissi_mensili)} />
        <Kpi label="Costi variabili" value={eur(data.costi_variabili)} />
        <Kpi label="BEP fatturato annuo" value={eur(data.bep_fatturato)} icon={Calculator} />
        <Kpi label="Cantieri attivi" value={`${data.cantieri_attivi}/${data.cantieri_totali}`} icon={HardHat} />
        <Kpi label="Cantieri in utile" value={data.cantieri?.length > 0 && data.cantieri.every(c => c.incidenza_disponibile) ? data.cantieri_in_utile : "Da configurare"} tone="good" />
        <Kpi label="Cantieri in perdita" value={data.cantieri?.length > 0 && data.cantieri.every(c => c.incidenza_disponibile) ? data.cantieri_in_perdita : "Da configurare"} />
        <Kpi label="Cantieri in ritardo" value={data.cantieri_in_ritardo} tone={data.cantieri_in_ritardo>0?"bad":"good"} icon={AlertTriangle} />
        <Kpi label="Ore lavorate" value={`${num(data.ore_lavorate)} h`} icon={Clock} />
        <Kpi label="Costo manodopera" value={eur(data.costo_manodopera)} />
        <Kpi label="Costo materiali" value={eur(data.costo_materiali)} />
        <Kpi label="Previsione incasso 30gg" value={eur(data.previsioni_incasso["30gg"])} tone="good" />
        <Kpi label="Previsione pagamento 30gg" value={eur(data.previsioni_pagamento["30gg"])} tone="bad" />
      </div>

      {/* Previsioni orizzonte */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="overline mb-4">Previsioni incassi & pagamenti</div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-2">Previsione INCASSI (crediti clienti)</div>
            <div className="space-y-2">
              {[["30gg","30 giorni"],["60gg","60 giorni"],["90gg","90 giorni"]].map(([k,l])=>(
                <div key={k} className="flex items-center justify-between border border-slate-100 p-2">
                  <span className="text-sm">{l}</span>
                  <span className="font-mono font-bold text-emerald-700">{eur(data.previsioni_incasso[k])}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-2">Previsione PAGAMENTI (fornitori + costi fissi)</div>
            <div className="space-y-2">
              {[["30gg","30 giorni"],["60gg","60 giorni"],["90gg","90 giorni"]].map(([k,l])=>(
                <div key={k} className="flex items-center justify-between border border-slate-100 p-2">
                  <span className="text-sm">{l}</span>
                  <span className="font-mono font-bold text-red-700">{eur(data.previsioni_pagamento[k])}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 p-5">
          <div className="overline mb-3">Ricavi vs Costi vs Guadagno per cantiere</div>
          <div className="w-full h-72">
            <ResponsiveContainer>
              <BarChart data={cantieriBar}>
                <XAxis dataKey="nome" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v/1000}k`} />
                <Tooltip formatter={(v) => eur(v)} />
                <Legend />
                <Bar dataKey="Ricavo" fill="#0f172a" />
                <Bar dataKey="Costo" fill="#94a3b8" />
                <Bar dataKey="Guadagno" fill="#ea580c" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-slate-200 p-5">
          <div className="overline mb-3">Andamento cantieri</div>
          <div className="space-y-2">
            {[
              ["in_anticipo","In anticipo","bg-emerald-100 text-emerald-800 border-emerald-200"],
              ["in_linea","In linea","bg-slate-100 text-slate-800 border-slate-200"],
              ["leggermente_in_ritardo","Leggerm. in ritardo","bg-yellow-100 text-yellow-800 border-yellow-200"],
              ["in_forte_ritardo","In forte ritardo","bg-red-100 text-red-800 border-red-200"],
            ].map(([k,l,c])=>{
              const n = data.cantieri.filter(x => x.andamento_temporale === k).length;
              return (
                <div key={k} className={`flex items-center justify-between p-3 border rounded-sm ${c}`}>
                  <span className="text-sm font-medium">{l}</span>
                  <span className="font-mono font-black">{n}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="overline">Elenco cantieri</div>
            <h3 className="font-display font-bold text-lg">Marginalità per cantiere</h3>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left p-3">Cantiere</th>
                <th className="text-left p-3">Cliente</th>
                <th className="text-left p-3">Stato</th>
                <th className="text-left p-3">Andamento</th>
                <th className="text-right p-3">Ricavo previsto</th>
                <th className="text-right p-3">Costo totale</th>
                <th className="text-right p-3">Guadagno</th>
                <th className="text-right p-3">Margine %</th>
                <th className="text-right p-3">Avanz.</th>
              </tr>
            </thead>
            <tbody>
              {data.cantieri.map((c) => (
                <tr key={c.cantiere_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-3">
                    <Link to={`/cantieri/${c.cantiere_id}`} className="font-medium text-slate-900 hover:text-orange-600">{c.nome}</Link>
                  </td>
                  <td className="p-3 text-slate-600">{c.cliente}</td>
                  <td className="p-3">
                    <span className="px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider bg-slate-100 border border-slate-200 rounded-sm">
                      {c.stato}
                    </span>
                  </td>
                  <td className="p-3 text-xs">{c.andamento_temporale?.replace(/_/g," ") || "-"}</td>
                  <td className="p-3 text-right font-mono">{eur(c.importo_totale_previsto)}</td>
                  <td className="p-3 text-right font-mono">{eur(c.costo_totale)}</td>
                  <td className={`p-3 text-right font-mono font-bold ${c.guadagno_netto >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {c.incidenza_disponibile ? eur(c.guadagno_netto) : "Da configurare"}
                  </td>
                  <td className="p-3 text-right font-mono">{c.incidenza_disponibile ? `${c.margine_netto_pct}%` : "Da configurare"}</td>
                  <td className="p-3 text-right">
                    <div className="inline-block w-24 h-2 bg-slate-100">
                      <div className="h-2 bg-orange-500" style={{ width: `${c.avanzamento_lavori}%` }} />
                    </div>
                    <div className="text-[10px] text-slate-500">{c.avanzamento_lavori}%</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FInput({ label, type = "text", value, onChange }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 block mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-xs" />
    </div>
  );
}
function FSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 block mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-xs">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function DashboardEconomicaCard({ admin }) {
  const anno = new Date().getFullYear();
  const { data } = useQuery({
    queryKey: ["dashboard-economica", anno],
    queryFn: () => api.get(`/analytics/dashboard-economica?anno=${anno}`).then((r) => r.data),
    staleTime: 30 * 1000,
  });
  if (!data) return null;
  const eur = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
  const STATO_BADGE = {
    "Superato":         "bg-emerald-100 text-emerald-800 border-emerald-300",
    "Raggiunto":        "bg-blue-100 text-blue-800 border-blue-300",
    "In avvicinamento": "bg-amber-100 text-amber-800 border-amber-300",
    "Non raggiunto":    "bg-rose-100 text-rose-800 border-rose-300",
  };

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="dashboard-economica">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="overline">Dashboard economica</div>
          <h2 className="font-display font-black text-xl">Sintesi anno {data.anno}</h2>
        </div>
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-sm text-xs font-bold border ${STATO_BADGE[data.bep_annuale_stato] || ""}`}>
          BEP annuale: {data.bep_annuale_stato}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KpiEcon label="Fatturato annuale prev." val={eur(data.fatturato_annuale_previsto)} />
        <KpiEcon label="Fatturato medio mese" val={eur(data.fatturato_mensile_medio)} />
        <KpiEcon label="Da cronoprogramma" val={eur(data.fatturato_annuale_da_cronoprogramma)} />
        <KpiEcon label="Effettivo" val={eur(data.fatturato_annuale_effettivo)} accent />
        <KpiEcon label="Costi di gestione annui" val={eur(data.costi_fissi_annuali)} />
        <KpiEcon label="Costi di gestione/mese" val={eur(data.costi_fissi_mensili)} />
        <KpiEcon label="Costi diretti" val={eur(data.costi_diretti_annuali)} />
        <KpiEcon label="Costo personale" val={eur(data.costo_personale_annuale)} />
        <KpiEcon label="Appalti terzi" val={eur(data.appalti_terzi_annui)} />
        <KpiEcon label="Spese generali" val={eur(data.spese_generali_annue)} sub={`${data.spese_generali_pct_media_pesata}% media`} />
        <KpiEcon label="Margine previsto" val={eur(data.margine_previsto_annuale)} good={data.margine_previsto_annuale > 0} />
        <KpiEcon label="Margine effettivo" val={eur(data.margine_effettivo_annuale)} good={data.margine_effettivo_annuale > 0} bad={data.margine_effettivo_annuale < 0} accent />
      </div>

      {/* BEP mese per mese */}
      <div className="border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-2">Mese</th>
              <th className="text-right p-2">Fatt. prev.</th>
              <th className="text-right p-2">Fatt. eff.</th>
              <th className="text-right p-2">C.fissi</th>
              <th className="text-right p-2">Personale</th>
              <th className="text-right p-2">Materiali</th>
              <th className="text-right p-2">S.gen.</th>
              <th className="text-right p-2">M.prev.</th>
              <th className="text-right p-2">M.eff.</th>
              <th className="text-right p-2">Pareggio</th>
              <th className="text-center p-2">Stato</th>
            </tr>
          </thead>
          <tbody>
            {data.bep_mensile.map((m) => (
              <tr key={m.mese} className="border-t border-slate-100">
                <td className="p-2 font-semibold">{m.label}</td>
                <td className="p-2 text-right font-mono">{eur(m.fatturato_previsto)}</td>
                <td className="p-2 text-right font-mono">{eur(m.fatturato_effettivo)}</td>
                <td className="p-2 text-right font-mono text-slate-500">{eur(m.costi_fissi)}</td>
                <td className="p-2 text-right font-mono text-slate-500">{eur(m.costo_personale)}</td>
                <td className="p-2 text-right font-mono text-slate-500">{eur(m.costo_materiali)}</td>
                <td className="p-2 text-right font-mono text-slate-500">{eur(m.spese_generali)}</td>
                <td className={`p-2 text-right font-mono ${m.margine_previsto >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{eur(m.margine_previsto)}</td>
                <td className={`p-2 text-right font-mono font-bold ${m.margine_effettivo >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{eur(m.margine_effettivo)}</td>
                <td className="p-2 text-right font-mono text-xs text-rose-700">{m.pareggio_mancante > 0 ? "-" + eur(m.pareggio_mancante) : "—"}</td>
                <td className="p-2 text-center"><span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-bold border ${STATO_BADGE[m.stato] || ""}`}>{m.stato}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Attività notevoli */}
      <div className="grid md:grid-cols-3 gap-3">
        <AttList label="Da fatturare" items={data.attivita_da_fatturare} tone="blue" admin={admin} />
        <AttList label="In ritardo" items={data.attivita_in_ritardo} tone="rose" admin={admin} />
        <AttList label="Attesa materiale" items={data.attivita_attesa_materiale} tone="slate" admin={admin} />
      </div>
    </section>
  );
}

function KpiEcon({ label, val, sub, accent, good, bad }) {
  return (
    <div className={`p-3 rounded-md border ${accent ? "bg-slate-900 text-white border-slate-900" : "bg-slate-50 border-slate-200"}`}>
      <div className={`text-[10px] uppercase tracking-widest ${accent ? "text-slate-400" : "text-slate-500"}`}>{label}</div>
      <div className={`font-display font-black text-lg mt-1 ${good ? "text-emerald-600" : ""} ${bad ? "text-rose-500" : ""}`}>{val}</div>
      {sub && <div className={`text-[10px] mt-0.5 ${accent ? "text-slate-400" : "text-slate-500"}`}>{sub}</div>}
    </div>
  );
}

function AttList({ label, items, tone, admin }) {
  const toneClass = { blue: "border-blue-200 bg-blue-50/40", rose: "border-rose-200 bg-rose-50/40", slate: "border-slate-200 bg-slate-50" }[tone];
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: (attivita_id) => api.delete(`/cronoprogramma/${attivita_id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Attività eliminata — sintesi economica e IVA aggiornate");
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["dashboard-economica"] });
      qc.invalidateQueries({ queryKey: ["programma-lavori-overview"] });
      qc.invalidateQueries({ queryKey: ["iva-riepilogo"] });
    },
    onError: (e) => {
      const status = e?.response?.status ? `HTTP ${e.response.status} · ` : "";
      toast.error(`Eliminazione fallita — ${status}${e?.response?.data?.detail || e.message || "Errore"}`);
    },
  });
  return (
    <div className={`border ${toneClass} rounded-md p-3`}>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-600 mb-2">{label} ({items.length})</div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400">Nessuna attività</div>
      ) : (
        <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
          {items.slice(0, 8).map((a) => (
            <li key={a.attivita_id} className="flex items-center justify-between gap-2 group" data-testid={`sintesi-att-${a.attivita_id}`}>
              <span className="truncate flex-1" title={a.nome}>{a.nome}</span>
              {a.importo > 0 && <span className="font-mono text-slate-500 shrink-0">€{Number(a.importo).toLocaleString("it-IT")}</span>}
              {admin && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(
                      `Eliminare l'attività "${a.nome}"?\n\n` +
                      `L'eliminazione aggiornerà automaticamente Cronoprogramma, Programma Lavori, sintesi economica e IVA Previsionale.`
                    )) {
                      del.mutate(a.attivita_id);
                    }
                  }}
                  disabled={del.isPending}
                  data-testid={`sintesi-del-${a.attivita_id}`}
                  title="Elimina attività"
                  className="p-1 rounded-sm text-rose-600 hover:bg-rose-100 disabled:opacity-50 shrink-0"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </li>
          ))}
          {items.length > 8 && <li className="text-slate-400 italic">+ altre {items.length - 8}…</li>}
        </ul>
      )}
    </div>
  );
}
